import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  onSnapshot,
  getFirestore,
  doc,
  getDoc,
} from "firebase/firestore";
import { UserAuth } from "../context/AuthContext";
import { loadStripe } from "@stripe/stripe-js";
import { usePurchasedPrices } from "../context/PurchasedPricesContext";

const PurchaseModal = ({ isOpen, onClose, muni }) => {
  const [products, setProducts] = useState([]);
  const { user } = UserAuth();
  const { purchasedPrices, loading: purchasedPricesLoading } =
    usePurchasedPrices();
  const [loading, setLoading] = useState(false);
  const [alreadyPurchased, setAlreadyPurchased] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const db = getFirestore();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productsRef = collection(db, "products");
        const activeProductsQuery = query(
          productsRef,
          where("active", "==", true)
        );
        const querySnapshot = await getDocs(activeProductsQuery);
        const products = {};

        const productPromises = querySnapshot.docs.map(async (productDoc) => {
          const productData = productDoc.data();
          const pricesRef = collection(productDoc.ref, "prices");
          const priceSnapshot = await getDocs(pricesRef);
          const prices = priceSnapshot.docs.map((doc) => ({
            priceId: doc.id,
            priceData: doc.data(),
            unit_amount: doc.data().unit_amount / 100,
          }));
          products[productDoc.id] = { ...productData, prices };
        });

        await Promise.all(productPromises);
        setProducts(products);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    const checkIfAlreadyPurchased = async () => {
      if (user && muni) {
        try {
          const paymentsRef = collection(db, "users", user.uid, "payments");
          const paymentsSnap = await getDocs(paymentsRef);

          const productIds = paymentsSnap.docs
            .filter((doc) => doc.data().status === "succeeded")
            .flatMap((doc) => {
              const data = doc.data();
              if (data.items && Array.isArray(data.items)) {
                return data.items
                  .map((item) => item.price?.product)
                  .filter(Boolean);
              }
              return [];
            });

          const uniqueProductIds = [...new Set(productIds)];
          const allProductIds = [];

          for (const productId of uniqueProductIds) {
            const productRef = doc(db, "products", productId);
            const productSnap = await getDoc(productRef);

            if (productSnap.exists()) {
              const productData = productSnap.data();
              allProductIds.push(productId);

              if (productData.metadata && productData.metadata.items) {
                const additionalItems = productData.metadata.items
                  .split(",")
                  .map((item) => item.trim());
                allProductIds.push(...additionalItems);
              }
            }
          }

          const finalUniqueProductIds = [...new Set(allProductIds)];
          setAlreadyPurchased(finalUniqueProductIds.includes(muni.prodId));
        } catch (error) {
          console.error("Error checking purchase status:", error);
        }
      }
      setInitialLoading(false);
    };

    fetchProducts();
    checkIfAlreadyPurchased();
  }, [db, user, muni]);

  const loadCheckout = async (priceId) => {
    if (!user) {
      alert("Please log in to purchase");
      return;
    }

    try {
      setLoading(true);
      const checkoutSessionRef = collection(
        db,
        "users",
        user.uid,
        "checkout_sessions"
      );
      const docRef = await addDoc(checkoutSessionRef, {
        mode: "payment",
        price: priceId,
        success_url: window.location.origin,
        cancel_url: window.location.origin,
      });

      onSnapshot(docRef, async (snap) => {
        const { error, sessionId } = snap.data();
        if (error) {
          alert(`An error occurred: ${error.message}`);
          setLoading(false);
        }
        if (sessionId) {
          const stripe = await loadStripe(
            process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
          );
          await stripe.redirectToCheckout({ sessionId });
          setLoading(false);
        }
      });
    } catch (error) {
      console.error("Error creating checkout session:", error);
      alert("Failed to create checkout session. Please try again.");
      setLoading(false);
    }
  };

  const isProductPurchased = (productId) => {
    return purchasedPrices.includes(productId);
  };

  if (!isOpen || !muni || purchasedPricesLoading || initialLoading) return null;

  const product = products[muni.prodId];
  if (!product) return null;

  const finalAlreadyPurchased =
    alreadyPurchased || isProductPurchased(muni.prodId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white p-4 rounded shadow-lg max-w-md w-full">
        <h2 className="text-xl font-bold mb-4">
          {finalAlreadyPurchased
            ? "Already Purchased"
            : `Purchase ${product.name}`}
        </h2>
        {finalAlreadyPurchased ? (
          <p>
            You have already purchased this content. Please refresh and enjoy!
          </p>
        ) : (
          <>
            <p>Please purchase below to access this content:</p>
            <div className="space-y-4 mt-4">
              {product.prices.map((price) => (
                <div
                  key={price.priceId}
                  className="flex justify-between items-center"
                >
                  <div>
                    <p>{product.name}</p>
                    <p>${price.unit_amount.toFixed(2)}</p>
                  </div>
                  <button
                    className="bg-blue-600 text-white rounded px-4 py-2 w-full sm:w-auto"
                    onClick={() => loadCheckout(price.priceId)}
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center space-x-2">
                        <span>Loading...</span>
                        <svg
                          aria-hidden="true"
                          className="w-5 h-5 text-white animate-spin"
                          viewBox="0 0 100 101"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="currentColor"
                          />
                          <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="#1C64F2"
                          />
                        </svg>
                      </div>
                    ) : (
                      "Purchase"
                    )}
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
        <button
          className="mt-4 bg-red-600 text-white rounded px-4 py-2"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default PurchaseModal;
